import * as T from 'three';
import {bladeStrideScale} from '../combat/bladeLocomotion';
import {DIRS8} from './anim';

type SolveLeg = (thigh:string,calf:string,foot:string,target:T.Vector3,pole:T.Vector3)=>void;
const LEGS = [['thigh_l','calf_l','foot_l'],['thigh_r','calf_r','foot_r']] as const;

/** Lengthen the PS run along its blended travel axis. Ankle height and sole
 * orientation belong to the authored gait; the existing anatomical IK solve
 * opens the stride without stretching bones or reversing knees. Applied before
 * terrain contact, and only to the run's actual mixer contribution. */
export class BladeStride {
  private readonly direction = new T.Vector3();
  private readonly forward = new T.Vector3();
  private readonly hip = new T.Vector3();
  private readonly ankle = new T.Vector3();
  private readonly delta = new T.Vector3();
  private readonly rotation = new T.Quaternion();
  private readonly targets = [new T.Vector3(),new T.Vector3()];

  apply(group:T.Object3D,body:T.Object3D,bones:ReadonlyMap<string,T.Bone>,
    weights:ReadonlyMap<string,number>,speed:number,solve:SolveLeg):void {
    this.direction.set(0,0,0);
    let weight=0;
    for(let i=0;i<DIRS8.length;i++){
      const w=weights.get(`sd_run_${DIRS8[i]}`)??0;
      weight+=w;
      this.direction.x+=Math.sin(i*Math.PI/4)*w;
      this.direction.z-=Math.cos(i*Math.PI/4)*w;
    }
    const gain=(bladeStrideScale(speed)-1)*Math.min(1,weight);
    if(gain<.001||this.direction.lengthSq()<.001)return;
    group.updateMatrixWorld(true);
    group.getWorldQuaternion(this.rotation);
    this.direction.normalize().applyQuaternion(this.rotation);
    this.forward.set(0,0,-1).applyQuaternion(this.rotation);
    for(let i=0;i<LEGS.length;i++){
      const names=LEGS[i],thigh=bones.get(names[0]),calf=bones.get(names[1]),foot=bones.get(names[2]);
      if(!thigh||!calf||!foot)return;
      thigh.getWorldPosition(this.hip);foot.getWorldPosition(this.ankle);
      const along=this.delta.copy(this.ankle).sub(this.hip).dot(this.direction);
      this.targets[i].copy(this.ankle).addScaledVector(this.direction,along*gain);
    }
    // A stride gets a steady, weight-blended stance offset. Solving a fresh
    // pelvis drop from the most extended leg every frame made the entire torso
    // jerk whenever the supporting leg changed or hit the reach limit.
    body.position.y-=.07*Math.min(1,gain/.19);
    group.updateMatrixWorld(true);
    for(let i=0;i<LEGS.length;i++){
      const [thigh,calf,foot]=LEGS[i];
      solve(thigh,calf,foot,this.targets[i],this.forward);
    }
  }
}
